/* ================================================================
   Navbar — Editorial refactor (matches design mockup MiniNav)
   - Transparent over hero (home page when at top)
   - Glass blur when scrolled or on inner pages
   - Preserves: i18n, auth, cart, AI button, search, language switch
   Reference: ikoffice-de/Ilkwang Office Redesign.html · pages/home.jsx
   ================================================================ */
import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useShop } from "../contexts/ShopContext";
import { useI18n } from "../contexts/I18nContext";
import { SearchSuggestionsPanel, useSearchSuggestions } from "./SearchSuggestions";
import { isAdminRole } from "../utils/roles";
import { BRAND } from "../config/brand";
import { themeNumber, LIGHT_HERO_THEMES } from "../pages/home/shared";

export default function Navbar() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchActiveIndex, setSearchActiveIndex] = useState(-1);
  const [scrolled, setScrolled] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const langRef = useRef(null);
  const searchRef = useRef(null);
  const mobileSearchRef = useRef(null);
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const isAdmin = isAdminRole(user?.role);
  const { cartCount } = useShop();
  const location = useLocation();
  const { lang, setLang, t, LANG_OPTIONS } = useI18n();
  const searchSuggestions = useSearchSuggestions(searchQuery, searchOpen, { includeRental: true, limit: 7 });

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (langRef.current && !langRef.current.contains(e.target)) setLangOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      const insideDesktop = searchRef.current && searchRef.current.contains(e.target);
      const insideMobile = mobileSearchRef.current && mobileSearchRef.current.contains(e.target);
      if (!insideDesktop && !insideMobile) setSearchOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    setSearchActiveIndex(-1);
  }, [searchQuery]);

  const closeSearch = () => {
    setSearchOpen(false);
    setSearchActiveIndex(-1);
  };

  const submitSearch = (value = searchQuery) => {
    const q = value.trim();
    if (!q) return;
    navigate(`/store?q=${encodeURIComponent(q)}`);
    setSearchQuery("");
    setMenuOpen(false);
    closeSearch();
  };

  const handleSearch = (e) => {
    e.preventDefault();
    submitSearch();
  };

  const pickSearchProduct = (product) => {
    if (!product?.slug) return;
    navigate(`/p/${product.slug}`);
    setSearchQuery("");
    setMenuOpen(false);
    closeSearch();
  };

  const pickSearchKeyword = (keyword) => {
    setSearchQuery(keyword);
    setSearchOpen(true);
  };

  const handleSearchKeyDown = (e) => {
    const products = searchSuggestions.products || [];
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSearchOpen(true);
      setSearchActiveIndex((idx) => (products.length ? (idx + 1) % products.length : -1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSearchActiveIndex((idx) => (products.length ? (idx <= 0 ? products.length - 1 : idx - 1) : -1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (searchActiveIndex >= 0 && products[searchActiveIndex]) {
        pickSearchProduct(products[searchActiveIndex]);
      } else {
        submitSearch();
      }
    } else if (e.key === "Escape") {
      closeSearch();
    }
  };

  const isHome = location.pathname === "/";
  // Home retains its own hero color; other pages use a solid brand header.
  const transparent = isHome && !scrolled && !menuOpen;
  // 밝은 히어로를 쓰는 클론 변형(shared.LIGHT_HERO_THEMES)은 투명 상태에서 어두운 글자.
  const lightTop = transparent && LIGHT_HERO_THEMES.has(themeNumber());
  const fgMain = lightTop ? "var(--ink-1000)" : "white";
  const fgSub = lightTop ? "rgba(15,18,26,0.72)" : "rgba(255,255,255,0.78)";

  const navStyle = {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    zIndex: 50,
    transition: "background 280ms var(--ease-out), backdrop-filter 280ms var(--ease-out), border-color 280ms var(--ease-out)",
    background: transparent ? "transparent" : "var(--clone-header)",
    backdropFilter: "none",
    WebkitBackdropFilter: transparent ? "none" : "blur(24px) saturate(180%)",
    borderBottom: transparent ? "1px solid transparent" : "1px solid rgba(255,255,255,0.08)",
    color: fgMain,
  };

  const linkStyle = {
    fontSize: 30.16,
    color: fgSub,
    transition: "color 0.2s",
  };

  const navLinks = [
    { to: "/store", label: t.store || "스토어" },
    { to: "/rental", label: t.rental || "렌탈" },
    { to: "/consultation", label: t.inquiry || "상담" },
    { to: "/compare", label: t.compare || "비교" },
  ];

  return (
    <nav style={navStyle}>
      <div
        className="mob-pad-tight"
        style={{
          maxWidth: 1240,
          margin: "0 auto",
          padding: "0 28px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          height: 56,
        }}
      >
        {/* Left: logo + nav */}
        <div style={{ display: "flex", alignItems: "center", gap: 32 }}>
          <Link
            to="/"
            style={{
              fontFamily: "var(--font-serif)",
              fontStyle: "italic",
              fontSize: "clamp(16px, 4.5vw, 20px)",
              fontWeight: 500,
              letterSpacing: "-0.02em",
              color: fgMain,
              whiteSpace: "nowrap",
            }}
          >
            {BRAND.logo}<span style={{ color: "var(--brand-400)" }}>.</span>
          </Link>
          <div className="nav-desktop-links" style={{ display: "flex", gap: 22, alignItems: "center" }}>
            {navLinks.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                style={linkStyle}
                onMouseEnter={(e) => (e.currentTarget.style.color = fgMain)}
                onMouseLeave={(e) => (e.currentTarget.style.color = fgSub)}
              >
                {item.label}
              </Link>
            ))}
          </div>
        </div>

        {/* Right: actions */}
        <div className="nav-desktop-actions" style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <form onSubmit={handleSearch} ref={searchRef} style={{ position: "relative" }}>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onFocus={() => setSearchOpen(true)}
              onKeyDown={handleSearchKeyDown}
              placeholder={t.search || "검색"}
              aria-autocomplete="list"
              style={{
                width: 148,
                fontSize: 12,
                padding: "6px 12px",
                borderRadius: 999,
                background: lightTop ? "rgba(15,18,26,0.06)" : "rgba(255,255,255,0.07)",
                color: fgMain,
                border: searchOpen
                  ? "1px solid rgba(112,184,255,0.55)"
                  : lightTop
                  ? "1px solid rgba(15,18,26,0.14)"
                  : "1px solid rgba(255,255,255,0.12)",
                outline: "none",
                boxShadow: searchOpen ? "0 0 0 3px rgba(0,113,227,0.18)" : "none",
              }}
            />
            {searchOpen && (
              <SearchSuggestionsPanel
                query={searchQuery}
                suggestions={searchSuggestions}
                activeIndex={searchActiveIndex}
                compact
                onPickProduct={pickSearchProduct}
                onPickKeyword={pickSearchKeyword}
                onSubmit={() => submitSearch()}
                style={{
                  position: "fixed",
                  top: 58,
                  left: "50%",
                  transform: "translateX(-50%)",
                  width: "min(540px, calc(100vw - 24px))",
                }}
              />
            )}
          </form>

          {/* Language switcher */}
          <div ref={langRef} style={{ position: "relative" }}>
            <button
              type="button"
              onClick={() => setLangOpen(!langOpen)}
              style={{
                fontSize: 12,
                color: fgSub,
                padding: "4px 8px",
                borderRadius: 6,
                display: "flex",
                alignItems: "center",
                gap: 4,
              }}
            >
              <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                <path d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-1.605.42-3.113 1.157-4.418" />
              </svg>
              <span>{LANG_OPTIONS?.find((l) => l.code === lang)?.flag || "🌐"}</span>
            </button>
            {langOpen && (
              <div
                style={{
                  position: "absolute",
                  right: 0,
                  top: "calc(100% + 6px)",
                  background: "rgba(8,10,28,0.92)",
                  backdropFilter: "blur(24px)",
                  borderRadius: 12,
                  border: "1px solid rgba(255,255,255,0.12)",
                  padding: 4,
                  minWidth: 140,
                  boxShadow: "var(--sh-3)",
                }}
              >
                {(LANG_OPTIONS || []).map((l) => (
                  <button
                    key={l.code}
                    type="button"
                    onClick={() => {
                      setLang(l.code);
                      setLangOpen(false);
                    }}
                    style={{
                      width: "100%",
                      padding: "8px 12px",
                      textAlign: "left",
                      fontSize: 12,
                      color: lang === l.code ? "var(--brand-400)" : "rgba(255,255,255,0.7)",
                      fontWeight: lang === l.code ? 600 : 400,
                      borderRadius: 8,
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                    }}
                  >
                    <span>{l.flag}</span>
                    <span>{l.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <Link
            to="/consultation"
            style={{
              padding: "5px 14px",
              background: "var(--sale-900)",
              color: "white",
              borderRadius: 999,
              fontSize: 11,
              fontWeight: 600,
            }}
          >
            {t.inquiry || "견적 문의"}
          </Link>
        </div>

        {/* Mobile menu toggle */}
        <button
          type="button"
          className="nav-mobile-toggle"
          onClick={() => setMenuOpen(!menuOpen)}
          style={{ color: fgMain, padding: 8 }}
          aria-label="menu"
        >
          <svg width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.6" viewBox="0 0 24 24">
            {menuOpen ? (
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
            )}
          </svg>
        </button>
      </div>

      {/* Mobile drawer */}
      {menuOpen && (
        <div
          style={{
            background: "rgba(8,10,18,0.96)",
            backdropFilter: "blur(24px)",
            borderTop: "1px solid rgba(255,255,255,0.08)",
            padding: "20px 28px 32px",
          }}
        >
          <form onSubmit={handleSearch} ref={mobileSearchRef} style={{ marginBottom: 16, position: "relative" }}>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onFocus={() => setSearchOpen(true)}
              onKeyDown={handleSearchKeyDown}
              placeholder={t.search || "검색"}
              aria-autocomplete="list"
              style={{
                width: "100%",
                fontSize: 14,
                padding: "10px 14px",
                borderRadius: 999,
                background: "rgba(255,255,255,0.07)",
                color: "white",
                border: searchOpen ? "1px solid rgba(112,184,255,0.55)" : "1px solid rgba(255,255,255,0.12)",
                outline: "none",
              }}
            />
            {searchOpen && (
              <SearchSuggestionsPanel
                query={searchQuery}
                suggestions={searchSuggestions}
                activeIndex={searchActiveIndex}
                onPickProduct={pickSearchProduct}
                onPickKeyword={pickSearchKeyword}
                onSubmit={() => submitSearch()}
                style={{
                  left: 0,
                  right: 0,
                  width: "100%",
                  maxHeight: "70vh",
                }}
              />
            )}
          </form>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {navLinks.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                onClick={() => setMenuOpen(false)}
                style={{
                  padding: "12px 4px",
                  fontSize: 16,
                  fontWeight: 500,
                  color: "rgba(255,255,255,0.85)",
                  borderBottom: "1px solid rgba(255,255,255,0.06)",
                }}
              >
                {item.label}
              </Link>
            ))}
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 16, flexWrap: "wrap" }}>
            {(LANG_OPTIONS || []).map((l) => (
              <button
                key={l.code}
                type="button"
                onClick={() => setLang(l.code)}
                style={{
                  padding: "6px 12px",
                  borderRadius: 999,
                  fontSize: 12,
                  background: lang === l.code ? "var(--sale-900)" : "rgba(255,255,255,0.08)",
                  color: lang === l.code ? "white" : "rgba(255,255,255,0.7)",
                  fontWeight: lang === l.code ? 600 : 400,
                }}
              >
                {l.flag} {l.label}
              </button>
            ))}
          </div>
          <div style={{ marginTop: 20, display: "flex", gap: 12, flexWrap: "wrap" }}>
            <Link
              to="/consultation"
              onClick={() => setMenuOpen(false)}
              style={{
                padding: "10px 18px",
                background: "var(--sale-900)",
                color: "white",
                borderRadius: 999,
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              {t.inquiry || "견적 문의"}
            </Link>
          </div>
        </div>
      )}

      {/* Responsive helpers (no Tailwind reliance for show/hide) */}
      <style>{`
        @media (max-width: 900px) {
          .nav-desktop-links, .nav-desktop-actions { display: none !important; }
        }
        @media (min-width: 901px) {
          .nav-mobile-toggle { display: none !important; }
        }
      `}</style>
    </nav>
  );
}
